import React, { memo, useEffect, useRef, useState } from 'react';
import type { NodeProps } from '@xyflow/react';
import { ArrowUp, Check, RotateCcw, Trash2, X } from 'lucide-react';
import { completarArroba, menciones, normalizarHilo, sugerenciasArroba } from '../graph/comentarios';
import type { FlowNode, Participante } from '../graph/types';
import { cuando, inicialDe, useComentarios } from '../panels/comentariosContext';

/** Resalta los @Nombre que existen entre los participantes. */
function TextoConArrobas({ texto, participantes }: { texto: string; participantes: Participante[] }) {
  const nombres = menciones(texto, participantes).map((p) => p.nombre);
  if (nombres.length === 0) return <>{texto}</>;
  const re = new RegExp(`(@(?:${nombres.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')}))`, 'gi');
  return (
    <>
      {texto.split(re).map((parte, i) =>
        re.test(parte) ? (
          <span key={i} className="rounded bg-[rgb(var(--brand-primary-rgb)_/_0.095)] px-1 font-medium text-brand-primary">
            {parte}
          </span>
        ) : (
          <React.Fragment key={i}>{parte}</React.Fragment>
        ),
      )}
    </>
  );
}

function Compositor({ onEnviar, participantes, autoFocus, placeholder }: { onEnviar: (t: string) => void; participantes: Participante[]; autoFocus?: boolean; placeholder: string }) {
  const [texto, setTexto] = useState('');
  const [cursor, setCursor] = useState(0);
  const ref = useRef<HTMLTextAreaElement>(null);
  const sug = sugerenciasArroba(texto, cursor, participantes);
  const completar = (nombre: string) => {
    const r = completarArroba(texto, cursor, nombre);
    setTexto(r.texto);
    setCursor(r.cursor);
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(r.cursor, r.cursor);
    });
  };
  const enviar = () => {
    if (!texto.trim()) return;
    onEnviar(texto);
    setTexto('');
    setCursor(0);
  };
  return (
    <div className="relative">
      {sug && sug.opciones.length > 0 && (
        <div className="absolute bottom-full left-0 z-10 mb-1 w-[300px] rounded-xl border border-black/[0.06] bg-white p-1 shadow-[0_12px_32px_rgb(var(--brand-black-rgb)_/_0.12)]">
          {sug.opciones.map((p) => (
            <button key={p.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => completar(p.nombre)} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-[15px] text-brand-black/90 hover:bg-surface-elevated">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary text-[10px] font-semibold text-white">{inicialDe(p.nombre)}</span>
              {p.nombre}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-end gap-1.5 rounded-2xl border border-black/[0.08] bg-white p-1 focus-within:border-brand-primary/50">
        <textarea
          ref={ref}
          autoFocus={autoFocus}
          value={texto}
          rows={1}
          placeholder={placeholder}
          onChange={(e) => {
            setTexto(e.target.value);
            setCursor(e.target.selectionStart ?? e.target.value.length);
          }}
          onSelect={(e) => setCursor((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (sug && sug.opciones.length > 0) completar(sug.opciones[0].nombre);
              else enviar();
            }
            e.stopPropagation();
          }}
          className="nodrag max-h-40 min-h-[48px] flex-1 resize-none bg-transparent px-3 py-2.5 text-[16px] text-brand-black/90 outline-none placeholder:text-neutral-400"
        />
        <button type="button" onClick={enviar} disabled={!texto.trim()} title="Enviar (Enter)" className="nodrag flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-primary text-white disabled:opacity-40">
          <ArrowUp className="h-3.5 w-3.5" />
        </button>
      </div>
      <p className="mt-2 px-1 text-[13px] text-neutral-400">Escribí @ para arrobar a alguien que haya entrado a este flujo.</p>
    </div>
  );
}

/** Pin de comentario: un círculo con la inicial; al tocarlo se abre el hilo al lado. */
export function NodoComentario({ id, data, selected }: NodeProps<FlowNode>) {
  const ctx = useComentarios();
  const { hilo, resuelto } = normalizarHilo(data.params);
  const abierto = ctx.abiertoId === id;
  const ultimo = hilo[hilo.length - 1];
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (abierto) cardRef.current?.scrollTo({ top: cardRef.current.scrollHeight });
  }, [abierto, hilo.length]);
  if (ctx.ocultarResueltos && resuelto && !abierto) return <div className="h-0 w-0" />;

  const cerrar = () => {
    ctx.abrir(null);
    if (hilo.length === 0) ctx.borrar(id); // se abrió y no se escribió nada: no queda un pin vacío
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (abierto) cerrar();
          else ctx.abrir(id);
        }}
        title={ultimo ? `${ultimo.autorNombre}: ${ultimo.texto}` : 'Comentario nuevo'}
        className={`relative flex h-11 w-11 items-center justify-center rounded-full rounded-bl-sm border-2 border-white text-[16px] font-semibold text-white shadow-[0_4px_14px_rgb(var(--brand-black-rgb)_/_0.18)] transition ${
          resuelto ? 'bg-neutral-400' : 'bg-brand-primary'
        } ${selected || abierto ? 'ring-2 ring-brand-primary/40' : ''}`}
      >
        {inicialDe(ultimo?.autorNombre ?? ctx.usuario.nombre)}
        {hilo.length > 1 && (
          <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-black px-1.5 text-[12px] text-white">{hilo.length}</span>
        )}
      </button>

      {abierto && (
        <div
          className="nodrag nopan nowheel absolute left-14 top-0 z-50 w-[480px] rounded-2xl rounded-tl-md border border-black/[0.06] bg-white shadow-[0_16px_48px_rgb(var(--brand-black-rgb)_/_0.16)]"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-1.5 border-b border-black/[0.05] px-4 py-3">
            <span className="flex-1 text-[16px] font-semibold text-brand-black/90">{hilo.length === 0 ? 'Nuevo comentario' : resuelto ? 'Resuelto' : 'Comentario'}</span>
            {hilo.length > 0 && (
              <button type="button" onClick={() => ctx.alternarResuelto(id)} title={resuelto ? 'Volver a abrir' : 'Marcar como resuelto'} className={`flex h-9 items-center gap-1.5 rounded-full px-3 text-[14px] font-medium ${resuelto ? 'text-neutral-600 hover:bg-surface-elevated' : 'bg-[#D1FAE5] text-[#065F46] hover:bg-[#A7F3D0]'}`}>
                {resuelto ? <RotateCcw className="h-3 w-3" /> : <Check className="h-3 w-3" />} {resuelto ? 'Reabrir' : 'Resolver'}
              </button>
            )}
            <button type="button" onClick={() => ctx.borrar(id)} title="Borrar el comentario" className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 hover:bg-[#FEE7E7] hover:text-[#B91C1C]">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
            <button type="button" onClick={cerrar} className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 hover:bg-surface-elevated hover:text-brand-black">
              <X className="h-4 w-4" />
            </button>
          </div>
          {hilo.length > 0 && (
            <div ref={cardRef} className="max-h-[380px] space-y-4 overflow-y-auto px-5 py-4">
              {hilo.map((m) => (
                <div key={m.id} className="flex gap-2">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-primary text-[15px] font-semibold text-white">{inicialDe(m.autorNombre)}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="truncate text-[15.5px] font-semibold text-brand-black/90">{m.autorNombre}</span>
                      <span className="text-[13px] text-neutral-400">{cuando(m.creadoEn)}</span>
                    </div>
                    <p className="whitespace-pre-wrap text-[16px] leading-normal text-brand-black/85">
                      <TextoConArrobas texto={m.texto} participantes={ctx.participantes} />
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="px-4 pb-4 pt-3">
            <Compositor onEnviar={(t) => ctx.agregarMensaje(id, t)} participantes={ctx.participantes} autoFocus placeholder={hilo.length === 0 ? 'Agregá un comentario…' : 'Responder…'} />
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(NodoComentario);
